import {
  DashboardCardNameEnum,
  FeedbackStatusEnum,
  RydeUiUnit,
  TDashboardCardFilterKey,
} from "@ryde/shared";

import { IDashboardSystemCardDefinition } from "./dynamic-dashboard.system-cards.models";

/** Rides grid control names — same for every rides-based card. */
const RIDE_FILTER_LINK_FIELDS: Partial<
  Record<TDashboardCardFilterKey, string>
> = {
  sites: "siteId",
  suppliers: "supplierId",
  vehicleTypes: "vehicleTypeId",
  rideTypes: "rideTypeId",
  courses: "courseId",
  drivers: "driverId",
  vehicles: "vehicleId",
  reasons: "reasonId",
  originStations: "originStationId",
  destinationStations: "destinationStationId",
  sourceAreas: "originAreaId",
  destinationAreas: "destinationAreaId",
};

/**
 * Dropdown source per filter key, so a card's filters reuse the same widgets as
 * report filters. Labels resolve from the shared `dropdownSource` entity names.
 */
export const SYSTEM_CARD_FILTER_SOURCES: Record<
  TDashboardCardFilterKey,
  RydeUiUnit
> = {
  sites: "site",
  suppliers: "supplier",
  units: "unit",
  passengerTypes: "passengerType",
  originStations: "station",
  destinationStations: "station",
  sourceAreas: "area",
  destinationAreas: "area",
  vehicleTypes: "vehicleType",
  rideTypes: "rideType",
  courses: "course",
  drivers: "driver",
  vehicles: "vehicle",
  reasons: "reason",
  passengers: "passenger",
  rides: "ride",
  feedbackSubjects: "feedbackSubject",
  feedbackSolutions: "feedbackSolution",
  createdUsers: "user",
};

/** Everything a rides-based card can be narrowed by; they all query the same entity. */
const RIDE_CARD_FILTERS: TDashboardCardFilterKey[] = [
  "sites",
  "suppliers",
  "vehicleTypes",
  "rideTypes",
  "courses",
  "drivers",
  "vehicles",
  "reasons",
  "originStations",
  "destinationStations",
  "sourceAreas",
  "destinationAreas",
];

export const DASHBOARD_SYSTEM_CARDS: IDashboardSystemCardDefinition[] = [
  {
    cardName: DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_OVERALL,
    title: "Rides Page - Manual Changes",
    linkTo: "/rides",
    supportedFilters: RIDE_CARD_FILTERS,
    filterToLinkField: RIDE_FILTER_LINK_FIELDS,
  },
  {
    cardName: DashboardCardNameEnum.OVERALL_COST,
    title: "Cost",
    linkTo: "/rides",
    supportedFilters: RIDE_CARD_FILTERS,
    filterToLinkField: RIDE_FILTER_LINK_FIELDS,
  },
  {
    cardName: DashboardCardNameEnum.STATUS_NEW_BOOKINGS,
    title: "Status 'New' Bookings",
    linkTo: "/bookings",
    supportedFilters: [
      "sites",
      "passengers",
      "originStations",
      "destinationStations",
      "reasons",
    ],
    filterToLinkField: {
      // The bookings grid has no `siteId` control of its own; a booking's site is
      // the passenger's, which is why the backend filters it through the relation
      // too. The control name is dotted, and its grid field name differs
      // (`passengerSiteName`) — another reason the link carries control names.
      sites: "passenger.siteId",
      passengers: "passengerId",
      originStations: "originStationId",
      destinationStations: "destinationStationId",
      reasons: "reasonId",
    },
  },
  {
    cardName: DashboardCardNameEnum.RIDE_PAGE_MANUAL_CHANGES_PER_USER,
    title: "Manual changes per User",
    linkTo: "/rides",
    supportedFilters: RIDE_CARD_FILTERS,
    filterToLinkField: RIDE_FILTER_LINK_FIELDS,
  },
  {
    cardName: DashboardCardNameEnum.VEHICLE_TYPE_RIDES_COST,
    title: "Vehicle Type/Rides/Cost",
    linkTo: "/rides",
    supportedFilters: RIDE_CARD_FILTERS,
    filterToLinkField: RIDE_FILTER_LINK_FIELDS,
  },
  {
    cardName: DashboardCardNameEnum.RIDES_SUPPLIERS,
    title: "Rides/Suppliers",
    linkTo: "/rides",
    supportedFilters: RIDE_CARD_FILTERS,
    filterToLinkField: RIDE_FILTER_LINK_FIELDS,
  },
  {
    cardName: DashboardCardNameEnum.PASSENGERS_WITHOUT_SITE,
    title: "Passengers without a site",
    linkTo: "/passengers",
    // `siteId: [null]` is the grid's "select none" value, i.e. "has no site" —
    // the same rows the KPI counts.
    linkFilters: { isActive: [true], siteId: [null] },
    // No site filter: "has no site" is the card's whole definition.
    supportedFilters: [
      "units",
      "passengerTypes",
      "originStations",
      "destinationStations",
    ],
    // The passengers grid names its stations differently from the rides grid.
    filterToLinkField: {
      units: "unitId",
      passengerTypes: "passengerTypeId",
      originStations: "sourceStation1Id",
      destinationStations: "destinationStation1Id",
    },
  },
  {
    cardName: DashboardCardNameEnum.OPEN_FEEDBACKS,
    title: "Open feedback",
    linkTo: "/feedbacks",
    // The positive list matching the backend's "not Closed and not Canceled" —
    // keep in step with `getOpenFeedbacks`.
    //
    // KNOWN EXCEPTION to "the page shows exactly what the card counted": this card
    // is the only KPI the PRD scopes to the picked date range, and a deep link can
    // carry filters but not a date range (the grid takes its range from the top
    // calendar, not a filter control). So the count is windowed while the page it
    // opens shows the whole backlog. Resolving it means either dropping the window
    // — which contradicts the PRD — or teaching the link to set the target
    // calendar; both are product calls, not implementation details.
    linkFilters: {
      status: [
        FeedbackStatusEnum.New,
        FeedbackStatusEnum.WaitingForSupplier,
        FeedbackStatusEnum.WaitingForAdmin,
      ],
    },
    // `createdUsers` is deliberately NOT offered even though the backend supports
    // it: the feedbacks grid's `createdUser` control is a non-filterable text
    // column holding a name, not a user id, so there is nothing to link it to.
    // Offering it would let the user narrow a card in a way that then drops every
    // filter from the link. Add it here once that grid gains a bound control.
    supportedFilters: [
      "sites",
      "passengers",
      "rides",
      "feedbackSubjects",
      "feedbackSolutions",
    ],
    filterToLinkField: {
      sites: "siteId",
      passengers: "passengerId",
      rides: "rideId",
      feedbackSubjects: "feedbackSubjectId",
      feedbackSolutions: "feedbackSolutionId",
    },
  },
  {
    cardName: DashboardCardNameEnum.PRICE_RATES_ZERO,
    title: "Price rates priced as 0",
    linkTo: "/price_rates",
    // "=:0" is FilterNumberComparison's "<operator>:<value>" encoding.
    linkFilters: { isActive: [true], basePrice: ["=:0"] },
    supportedFilters: [
      "sites",
      "suppliers",
      "vehicleTypes",
      "sourceAreas",
      "destinationAreas",
    ],
    filterToLinkField: {
      sites: "siteId",
      suppliers: "supplierId",
      vehicleTypes: "vehicleTypeId",
      sourceAreas: "sourceAreaId",
      destinationAreas: "destinationAreaId",
    },
  },
];
